// src/pages/ResetPassword.jsx
// 3.40.1: our own "Choose a new password" page for the "Reset your password"
// email (Forgot password?, or an Admin's Password… → reset email). It used
// to open Firebase's built-in page, which looks different and accepts any
// password of 6 characters; here the portal's own rule applies (8 or more,
// letters and numbers). The link is /portal/reset-password?oobCode=…; the
// code works once, for an hour. Firebase checks and uses it exactly as its
// own page did (verifyPasswordResetCode / confirmPasswordReset).
import { useEffect, useState } from 'react';
import { trackPage } from '../utils/visits';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase/config';
import { pathForTab, DEFAULT_TAB } from '../utils/tabRoutes';
import AuthShell, { A, AuthMessage, AuthButton, PasswordField } from '../components/AuthShell';
import { passwordProblem } from './ChooseOwnPassword';

const KEY = 'reset-password.code';

// The code from the link. It's taken out of the address bar straight away
// (kept for this browser tab only), so it isn't left in the history.
export function resetPasswordCodeFromLocation() {
  if (!/\/reset-password\/?$/.test(window.location.pathname)) return null;
  const fromLink = new URLSearchParams(window.location.search).get('oobCode');
  try {
    if (fromLink) {
      sessionStorage.setItem(KEY, fromLink);
      window.history.replaceState(null, '', window.location.pathname);
      return fromLink;
    }
    return sessionStorage.getItem(KEY) || '';
  } catch { return fromLink || ''; }
}

const LINK_PROBLEM = {
  'auth/expired-action-code': 'This link has expired. Reset links work for one hour. Ask for a new one with "Forgot password?" on the sign-in page.',
  'auth/invalid-action-code': 'This link has already been used, or a newer reset email replaced it. Use the newest email, or ask for a new link with "Forgot password?".',
  'auth/user-disabled': 'This account has been switched off. Please contact your administrator.',
  'auth/user-not-found': "We couldn't find this account any more. Please contact your administrator.",
};

export default function ResetPassword({ code }) {
  useEffect(() => { trackPage('reset-password'); }, []);   // 3.49.0: Analytics (visitors before signing in)
  const [state, setState] = useState(code ? 'checking' : 'problem');   // checking | form | saving | done | problem
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [again, setAgain] = useState('');
  const [message, setMessage] = useState(code ? '' : LINK_PROBLEM['auth/invalid-action-code']);

  useEffect(() => {
    if (!code) return;
    verifyPasswordResetCode(auth, code)
      .then(e => { setEmail(e); setState('form'); })
      .catch(e => { setMessage(LINK_PROBLEM[e.code] || "This link can't be used. Ask for a new one with \"Forgot password?\" on the sign-in page."); setState('problem'); });
  }, [code]);

  async function submit(e) {
    e.preventDefault();
    setMessage('');
    const bad = passwordProblem(pw);
    if (bad) { setMessage(bad); return; }
    if (pw !== again) { setMessage("The two passwords don't match."); return; }
    setState('saving');
    try {
      await confirmPasswordReset(auth, code, pw);
      try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
      setState('done');
    } catch (e2) {
      if (LINK_PROBLEM[e2.code]) { setMessage(LINK_PROBLEM[e2.code]); setState('problem'); return; }
      setMessage(e2.code === 'auth/weak-password' ? 'Please choose a stronger password: at least 8 characters, with letters and numbers.'
        : e2.code === 'auth/network-request-failed' ? "Can't reach the sign-in service. Check your internet connection and try again."
        : "We couldn't save your password. Please try again.");
      setState('form');
    }
  }

  const goToSignIn = () => {
    try { sessionStorage.removeItem(KEY); } catch { /* ignore */ }
    window.location.assign(pathForTab(DEFAULT_TAB));
  };

  return (
    <AuthShell stack viewKey={state === 'saving' ? 'form' : state}>
      <h1 style={S.title}>{state === 'done' ? 'Password changed' : 'Choose a new password'}</h1>

      {state === 'checking' && <p style={S.text}>Checking your link…</p>}

      {(state === 'form' || state === 'saving') && (
        <form onSubmit={submit} style={S.form} noValidate>
          <p style={S.text}>For <strong>{email}</strong>. Use at least 8 characters, with letters and numbers.</p>
          <input type="email" value={email} readOnly hidden autoComplete="username" />
          <label style={S.label}>New password
            <PasswordField value={pw} onChange={e => setPw(e.target.value)} autoFocus autoComplete="new-password"
              placeholder="At least 8 characters, letters and numbers" />
          </label>
          <label style={S.label}>Type it again
            <PasswordField value={again} onChange={e => setAgain(e.target.value)} autoComplete="new-password" />
          </label>
          {message && <AuthMessage tone="error">{message}</AuthMessage>}
          <AuthButton busy={state === 'saving'} busyLabel="Saving…">Save new password</AuthButton>
        </form>
      )}

      {state === 'done' && (
        <>
          <AuthMessage tone="success">Your password is changed. Sign in with <strong>{email}</strong> and your new password.</AuthMessage>
          <button onClick={goToSignIn} className="auth-btn">Go to sign in</button>
        </>
      )}

      {state === 'problem' && (
        <>
          <AuthMessage tone="error">{message}</AuthMessage>
          <button onClick={goToSignIn} className="auth-btn">Go to sign in</button>
        </>
      )}
    </AuthShell>
  );
}

const S = {
  title: { ...A.title, margin: 0 },
  text: { ...A.text, margin: 0 },
  form: { display: 'flex', flexDirection: 'column', gap: 12 },
  label: { display: 'flex', flexDirection: 'column', gap: 6, ...A.label },
};
